import ServicePageLayout from './ServicePageLayout';
import { Scale, Shield, Users, FileText, AlertTriangle, Gavel, BookOpen, MessageSquare } from 'lucide-react';

const relatedServices = [
  { path: '/conseil-rh/', label: 'Conseil RH Particuliers' },
  { path: '/formation-rh/', label: 'Formations RH' },
  { path: '/audit-rh/', label: 'Audit RH' },
  { path: '/accompagnement-collectivites/', label: 'Accompagnement Collectivités' },
];

const expertises = [
  {
    icon: Scale,
    title: 'Conseil en droit du travail',
    description: 'Accompagnement juridique complet sur toutes les questions de droit du travail : rédaction et analyse de contrats, gestion des litiges, conformité réglementaire et veille juridique. Je traduis le jargon juridique en conseils actionnables pour votre entreprise.',
  },
  {
    icon: Shield,
    title: 'Prévention et gestion des risques',
    description: 'Identification proactive des risques juridiques dans vos pratiques RH. J\'évalue vos processus existants et vous propose des solutions pour anticiper les contentieux et sécuriser juridiquement votre organisation.',
  },
  {
    icon: Users,
    title: 'Médiation et résolution des conflits',
    description: 'Intervention en tant que tiers neutre pour faciliter le dialogue entre parties en conflit. J\'analyse la situation, identifie les enjeux sous-jacents et accompagne les parties vers une solution consensuelle et durable.',
  },
  {
    icon: FileText,
    title: 'Rédaction de documents juridiques',
    description: 'Rédaction et révision de contrats de travail, avenants, règlements intérieurs, notes de service et tous documents RH. Chaque document est adapté à votre contexte spécifique et conforme à la réglementation en vigueur.',
  },
  {
    icon: AlertTriangle,
    title: 'Gestion des situations sensibles',
    description: 'Accompagnement dans les situations délicates : licenciements, ruptures conventionnelles, harcèlement, accident du travail. Je vous aide à naviguer dans ces moments critiques avec rigueur juridique et humanité.',
  },
  {
    icon: Gavel,
    title: 'Représentation aux Prud\'hommes',
    description: 'Préparation et accompagnement dans les procédures prud\'homales. J\'aide à constituer le dossier, préparer les arguments et vous accompagne tout au long de la procédure pour défendre vos intérêts.',
  },
  {
    icon: BookOpen,
    title: 'Formation droit social pour managers',
    description: 'Sessions de formation sur mesure pour vos équipes managériales : bases du droit du travail, gestion des absences, procédures disciplinaires, obligations de l\'employeur. Des formations pratiques et interactives.',
  },
  {
    icon: MessageSquare,
    title: 'Veille juridique et alertes',
    description: 'Suivi continu des évolutions législatives et réglementaires en droit social. Je vous alerte des changements impactant votre secteur et vous accompagne dans leur mise en œuvre opérationnelle.',
  },
];

export default function DroitSocialPage() {
  return (
    <ServicePageLayout
      title="Expertise Droit Social et Contentieux | Aurore RH"
      description="Expertise en droit social pour entreprises et collectivités : conseil juridique, gestion des conflits et rédaction de documents."
      canonical="https://aurorerh.fr/droit-social/"
      relatedServices={relatedServices}
    >
      <div className="prose dark:prose-invert max-w-none mb-16">
        <p className="text-lg text-warm-gray-700 dark:text-warm-gray-300 leading-relaxed mb-6">
          Le droit social est un domaine en constante évolution, complexe et technique. Une erreur d'appréciation 
          peut avoir des conséquences financières et humaines lourdes pour votre organisation. En tant que consultante 
          RH avec une maîtrise en droit public, je combine expertise juridique solide et compréhension des réalités 
          opérationnelles pour vous accompagner efficacement.
        </p>
        <p className="text-lg text-warm-gray-700 dark:text-warm-gray-300 leading-relaxed mb-6">
          Mon approche du droit social est pragmatique : je ne me contente pas de vous citer la loi, je vous explique 
          ce qu'elle signifie concrètement pour votre situation et je vous propose des solutions actionnables. 
          Que vous soyez une PME, une collectivité territoriale ou une grande entreprise, je m'adapte à vos 
          contraintes et à votre culture d'entreprise.
        </p>
      </div>

      <h2 className="text-3xl font-bold text-warm-gray-900 dark:text-warm-gray-100 mb-10">
        Nos domaines d'expertise en droit social
      </h2>

      <div className="space-y-8 mb-16">
        {expertises.map((item, index) => (
          <div
            key={index}
            className="flex gap-5 p-6 bg-white dark:bg-warm-gray-800 rounded-2xl shadow-sm hover:shadow-md transition-shadow duration-300 border border-aurore-light/20 dark:border-warm-gray-700"
          >
            <div className="flex-shrink-0 w-14 h-14 bg-aurore-light/40 dark:bg-warm-gray-700 rounded-xl flex items-center justify-center">
              <item.icon className="w-7 h-7 text-aurore-dark dark:text-aurore-light" />
            </div>
            <div>
              <h3 className="text-xl font-semibold text-warm-gray-900 dark:text-warm-gray-100 mb-2">
                {item.title}
              </h3>
              <p className="text-warm-gray-700 dark:text-warm-gray-300 leading-relaxed">
                {item.description}
              </p>
            </div>
          </div>
        ))}
      </div>

      <h2 className="text-3xl font-bold text-warm-gray-900 dark:text-warm-gray-100 mb-6">
        Pourquoi faire appel à une experte en droit social ?
      </h2>
      <div className="grid sm:grid-cols-2 gap-6 mb-16">
        {[
          {
            title: 'Maîtrise en droit public',
            desc: 'Une formation académique solide qui garantit une expertise juridique rigoureuse et à jour.',
          },
          {
            title: 'Approche opérationnelle',
            desc: 'Je traduis le droit en actions concrètes adaptées à votre contexte organisationnel.',
          },
          {
            title: 'Indépendance totale',
            desc: 'Consultante freelance sans lien avec votre structure, je peux vous dire les choses franchement.',
          },
          {
            title: 'Réactivité',
            desc: 'Disponibilité et réponse rapide pour les situations urgentes nécessitant une décision immédiate.',
          },
        ].map((item, i) => (
          <div key={i} className="p-5 bg-warm-gray-50 dark:bg-warm-gray-800/50 rounded-xl">
            <h3 className="font-semibold text-warm-gray-900 dark:text-warm-gray-100 mb-2">{item.title}</h3>
            <p className="text-warm-gray-700 dark:text-warm-gray-300 text-sm">{item.desc}</p>
          </div>
        ))}
      </div>

      <h2 className="text-3xl font-bold text-warm-gray-900 dark:text-warm-gray-100 mb-6">
        Secteurs d'intervention
      </h2>
      <p className="text-warm-gray-700 dark:text-warm-gray-300 leading-relaxed mb-6">
        J'interviens auprès de structures de toutes tailles et de tous secteurs : entreprises privées (PME, ETI, grands groupes), 
        collectivités territoriales, associations, et établissements publics. Chaque secteur ayant ses spécificités 
        juridiques, je m'adapte en continu à vos contraintes particulières.
      </p>
      <div className="flex flex-wrap gap-3 mb-16">
        {['PME et startups', 'ETI', 'Grands groupes', 'Collectivités territoriales', 'Associations', 'Etablissements publics'].map((tag) => (
          <span key={tag} className="px-4 py-2 bg-aurore-light/30 dark:bg-warm-gray-700 text-aurore-dark dark:text-aurore-light rounded-full text-sm font-medium">
            {tag}
          </span>
        ))}
      </div>
    </ServicePageLayout>
  );
}
